import {useState} from 'react'
import {useTranslation} from 'react-i18next'
import {Button} from '@/components/ui/button'
import {LockGlyph} from '@/components/ui/icons'
import {Tag} from '@/components/ui/tag'
import {DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger} from '@/components/ui/dropdown-menu'
import {ReporteHistorialDialog} from '@/components/bateria/informe/reporte-historial-dialog'
import {
  useDownloadReporte,
  useStartReporte,
  type BateriaEvaluacion,
  type BateriaReporteAlcance,
} from '@/hooks/bateria/use-bateria'
import {useDownloadReporteReservado} from '@/hooks/bateria/use-bateria-reportes'
import {translateApiError} from '@/lib/errors'

/** El estado persistido solo espeja el alcance consolidado. El informe reservado (Res. 2764/2022,
 * art. 5, parágrafo) se baja acá porque la pantalla es clínica (`require_system_user`) y no se reenvía. */
export function ReporteAcciones({
  businessId,
  evaluacionId,
  evaluacion,
  alcance,
}: {
  businessId: string
  evaluacionId: string
  evaluacion: BateriaEvaluacion
  alcance: BateriaReporteAlcance
}) {
  const {t} = useTranslation()
  const start = useStartReporte(businessId, evaluacionId)
  const download = useDownloadReporte(businessId, evaluacionId)
  const reservado = useDownloadReporteReservado(businessId, evaluacionId)
  const [error, setError] = useState<string | null>(null)
  const [errorReservado, setErrorReservado] = useState<string | null>(null)
  const [scopedNotice, setScopedNotice] = useState(false)
  const [historialAbierto, setHistorialAbierto] = useState(false)

  const status = evaluacion.reporte_status
  const esConsolidado = alcance === 'consolidado'
  const running = esConsolidado && (status === 'pendiente' || status === 'procesando')
  const desactualizado = esConsolidado && status === 'listo' && evaluacion.reporte_desactualizado

  function generar() {
    setError(null)
    setScopedNotice(false)
    start.mutate(alcance, {
      onSuccess: () => {
        if (!esConsolidado) setScopedNotice(true)
      },
      onError: (err) => setError(translateApiError(err)),
    })
  }

  function descargar() {
    setError(null)
    download.mutate(undefined, {onError: (err) => setError(translateApiError(err))})
  }

  function descargarReservado() {
    setErrorReservado(null)
    reservado.mutate(undefined, {onError: (err) => setErrorReservado(translateApiError(err))})
  }

  return (
    <div className="flex flex-col items-end gap-1.5">
      <div className="flex items-center gap-2">
        <Button size="sm" data-testid="informe-generar" disabled={running || start.isPending} onClick={generar}>
          {esConsolidado && status === 'listo'
            ? t('admin.bateria.reporte.regenerate')
            : t('admin.bateria.reporte.generate')}
        </Button>
        {esConsolidado && running && (
          <Tag tone="amber" data-testid="informe-reporte-status">
            {t(`admin.bateria.reporte.status.${status}`)}
          </Tag>
        )}
        {esConsolidado && status === 'listo' && (
          <>
            <Tag tone={desactualizado ? 'amber' : 'green'} data-testid="informe-reporte-status">
              {t(desactualizado ? 'admin.bateria.reporte.status.desactualizado' : 'admin.bateria.reporte.status.listo')}
            </Tag>
            <Button
              size="sm"
              variant="outline"
              data-testid="informe-descargar"
              disabled={download.isPending}
              onClick={descargar}>
              {t('admin.bateria.reporte.download')}
            </Button>
          </>
        )}
        {esConsolidado && status === 'error' && (
          <Tag tone="red" data-testid="informe-reporte-status">
            {t('admin.bateria.reporte.status.error')}
          </Tag>
        )}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              size="icon-xs"
              variant="ghost"
              data-testid="informe-acciones-kebab"
              aria-label={t('admin.bateria.detail.masAcciones')}>
              <span aria-hidden="true" className="text-base leading-none">
                ⋯
              </span>
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent>
            <DropdownMenuItem data-testid="informe-regenerar" onSelect={generar}>
              {t('admin.bateria.informe.acciones.regenerar')}
            </DropdownMenuItem>
            <DropdownMenuItem data-testid="informe-ver-historial" onSelect={() => setHistorialAbierto(true)}>
              {t('admin.bateria.informe.acciones.historial.abrir')}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
      {desactualizado && (
        <p
          className="notice-amber max-w-xs rounded-lg px-3 py-2 text-right text-xs leading-snug"
          data-testid="informe-desactualizado">
          {t('admin.bateria.bandaDeVerdad.informeDesactualizado')}
        </p>
      )}
      {esConsolidado && status === 'listo' && (
        <div
          className="notice-amber flex max-w-xs flex-col items-end gap-1.5 rounded-lg px-3 py-2"
          data-testid="informe-reservado">
          <Button
            size="sm"
            variant="outline"
            data-testid="informe-reservado-descargar"
            disabled={reservado.isPending}
            onClick={descargarReservado}>
            <span data-icon="inline-start" className="text-tone-amber-fg">
              <LockGlyph />
            </span>
            {t('admin.bateria.reporte.reservado.download')}
          </Button>
          <p className="text-right text-xs leading-snug">{t('admin.bateria.reporte.reservado.hint')}</p>
          {errorReservado && (
            <span className="text-right text-xs font-medium" data-testid="informe-reservado-error">
              {errorReservado}
            </span>
          )}
        </div>
      )}
      {!esConsolidado && scopedNotice && (
        <p className="max-w-xs text-right text-xs text-muted-foreground" data-testid="informe-generar-alcance-nota">
          {t('admin.bateria.informe.acciones.generadoSinDescarga')}
        </p>
      )}
      {error && <span className="text-xs text-destructive">{error}</span>}
      <ReporteHistorialDialog
        businessId={businessId}
        evaluacionId={evaluacionId}
        open={historialAbierto}
        onOpenChange={setHistorialAbierto}
      />
    </div>
  )
}
